import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV } from "@/constants/testIds";
import { scrollToId } from "@/hooks/useSmoothScroll";

const LINKS = [
  { id: "home", label: "Home", testid: NAV.linkHome },
  { id: "about", label: "About", testid: NAV.linkAbout },
  { id: "approach", label: "Approach", testid: NAV.linkApproach },
  { id: "credentials", label: "Credentials", testid: NAV.linkCredentials },
  { id: "faq", label: "FAQ", testid: NAV.linkFaq },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleClick = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), 60);
  };

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-background/75 border-b border-border/60"
          : "bg-transparent"
      }`}
      data-testid={NAV.container}
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 py-5 flex items-center justify-between">
        <button
          onClick={() => handleClick("home")}
          data-testid={NAV.logo}
          className="flex items-center gap-2 group"
        >
          <span className="w-2 h-2 rounded-full bg-primary group-hover:scale-125 transition-transform" />
          <span className="font-serif text-xl md:text-2xl text-foreground tracking-tight">
            Neha Gupta
          </span>
          <span className="hidden md:inline text-xs uppercase tracking-[0.25em] text-muted-foreground ml-2 mt-1">
            Psychotherapy
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={l.testid}
              onClick={() => handleClick(l.id)}
              className="text-sm text-foreground/80 hover:text-foreground transition-colors underline-grow"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid={NAV.ctaBook}
            onClick={() => handleClick("contact")}
            className="ml-2 px-5 py-2.5 rounded-full bg-foreground text-background text-sm hover:bg-primary transition-colors duration-500"
          >
            Book a Session
          </button>
        </nav>

        <button
          className="md:hidden p-2 -mr-2 text-foreground"
          onClick={() => setOpen((v) => !v)}
          data-testid={NAV.mobileToggle}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            key="mobile"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden bg-background/95 backdrop-blur-xl border-t border-border"
            data-testid={NAV.mobileMenu}
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  onClick={() => handleClick(l.id)}
                  data-testid={`${l.testid}-mobile`}
                  className="text-left py-3 border-b border-border/50 text-foreground/80 font-serif text-2xl"
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={() => handleClick("contact")}
                data-testid={`${NAV.ctaBook}-mobile`}
                className="mt-4 px-5 py-3.5 rounded-full bg-foreground text-background text-sm text-center"
              >
                Book a Session
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
